// 210 variant of validator.ts's per-transaction content checks: walks a
// parsed 210 (header, a repeating Stop (LX) loop each nesting its own Name
// loop, dates and freight/accessorial charge lines, and an L3 summary) into
// its flat segment list, then runs the shared dictionary-driven element
// checks against it, plus the structural checks specific to a 210 (missing
// B3, no N1 loop anywhere in the document, no LX stop loop, and a mismatch
// between L3's declared total charge and the sum of every L1 charge line
// across every stop).

import type {EdiElementValue, EdiSegment} from './tokenizer';
import {
  checkElements,
  type PathedSegment,
  type ValidationFinding,
} from './validator';
import type {NameLoop210, Parsed210, StopLoop210} from './parser210';

function elementValueAsString(
  value: EdiElementValue | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value.join('') : value;
}

function collectNameLoopSegments210(
  loop: NameLoop210,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectStopSegments210(
  stop: StopLoop210,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: stop.lx, path: `${path}.lx`}];
  stop.nameLoops.forEach((loop, i) =>
    out.push(...collectNameLoopSegments210(loop, `${path}.nameLoops[${i}]`))
  );
  stop.g62.forEach((s, i) => out.push({segment: s, path: `${path}.g62[${i}]`}));
  if (stop.l5) out.push({segment: stop.l5, path: `${path}.l5`});
  if (stop.l0) out.push({segment: stop.l0, path: `${path}.l0`});
  stop.l1.forEach((s, i) => out.push({segment: s, path: `${path}.l1[${i}]`}));
  return out;
}

export function collectParsedSegments210(
  parsed: Parsed210,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.b3, 'header.b3');
  push(header.c3, 'header.c3');
  push(header.itd, 'header.itd');
  pushAll(header.refs, 'header.refs');
  pushAll(header.g62, 'header.g62');
  header.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments210(loop, `${basePath}.header.nameLoops[${i}]`)
    );
  });

  parsed.stops.forEach((stop, i) => {
    out.push(...collectStopSegments210(stop, `${basePath}.stops[${i}]`));
  });

  pushAll(parsed.summary.l3, 'summary.l3');

  return out;
}

export function validateParsed210(
  parsed: Parsed210,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.b3) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.b3`,
      message:
        "Missing B3 segment — every 210 must open with a Beginning Segment for Carrier's Invoice.",
    });
  }

  const hasHeaderNameLoop = parsed.header.nameLoops.length > 0;
  const hasStopNameLoop = parsed.stops.some(stop => stop.nameLoops.length > 0);
  if (!hasHeaderNameLoop && !hasStopNameLoop) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.nameLoops`,
      message:
        'No N1 party loops found anywhere in this motor carrier freight details and invoice — neither at header level nor on any stop.',
    });
  }

  if (parsed.stops.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.stops`,
      message:
        'No LX stop loops found in this motor carrier freight details and invoice.',
    });
  }

  if (parsed.summary.l3.length > 0) {
    const declaredSegment =
      parsed.summary.l3.find(
        s => elementValueAsString(s.elements[4]) !== undefined
      ) ?? parsed.summary.l3[0];
    const declared = elementValueAsString(declaredSegment.elements[4]);
    if (declared !== undefined && declared.trim() !== '') {
      let hasUnparsableCharge = false;
      const summedCharges = parsed.stops.reduce((sum, stop, stopIndex) => {
        const stopTotal = stop.l1.reduce((lineSum, l1, l1Index) => {
          const amount = elementValueAsString(l1.elements[3]);
          if (amount === undefined || amount.trim() === '') return lineSum;
          const amountNumber = Number(amount);
          if (Number.isNaN(amountNumber)) {
            hasUnparsableCharge = true;
            findings.push({
              severity: 'error',
              path: `${txPath}.stops[${stopIndex}].l1[${l1Index}]`,
              message: `L104 freight/accessorial charge amount "${amount}" is not a valid number.`,
            });
            return lineSum;
          }
          return lineSum + amountNumber;
        }, 0);
        return sum + stopTotal;
      }, 0);
      const declaredNumber = Number(declared);
      if (
        !hasUnparsableCharge &&
        !Number.isNaN(declaredNumber) &&
        Math.abs(declaredNumber - summedCharges) > 0.001
      ) {
        const declaredIndex = parsed.summary.l3.indexOf(declaredSegment);
        findings.push({
          severity: 'error',
          path: `${txPath}.summary.l3[${declaredIndex}]`,
          message: `L305 says a total charge of ${declared} but the L1 freight/accessorial charge lines across every stop sum to ${summedCharges}.`,
        });
      }
    }
  }

  for (const {segment, path} of collectParsedSegments210(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}
